import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Yup from 'yup';
import {
  Modal,
  ModalButtons,
  ModalContent,
  Tab,
  TabItem,
  Button,
  ProgressBar,
  withForm,
} from 'mw-style-react';
import { DEL_MODAL } from 'constants';
import {
  CREATE_REF_BOOK,
  UPDATE_REF_BOOK,
} from '@control-front-end/common/constants/refBooks';
import useIntl from 'useIntl';
import mes from './intl';
import RefBookSettings from './components/RefBookSettings';
import RefBookUsage from './components/RefBookUsage';
import RefBookAccess from './components/RefBookAccess';
// eslint-disable-next-line no-unused-vars
import m from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import l from './CreateRefBook.scss'; // NOSONAR

function CreateRefBooksBase(props) {
  const {
    data: { id, ownerId, mode },
    values,
    handleSubmit,
    handleOnChange,
    onContentChange,
    visibility,
    onClose,
    isSubmit,
    errors,
  } = props;
  const t = useIntl();
  const [activeTab, setActiveTab] = useState('settings');
  const [editMode, setEditMode] = useState(mode === 'edit');

  const handleDataChange = (data) => {
    handleOnChange(data);
    onContentChange();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'settings':
        return (
          <RefBookSettings
            editMode={editMode}
            values={values}
            errors={errors}
            handleOnChange={handleDataChange}
          />
        );
      case 'access':
        return (
          <RefBookAccess
            editMode={editMode}
            values={values}
            ownerId={ownerId}
            handleOnChange={handleDataChange}
          />
        );
      case 'usage':
        return <RefBookUsage refBookId={id} />;
      default:
        return null;
    }
  };

  const getModalTitle = () => {
    let modalTitle;
    if (!id) {
      modalTitle = t(mes.createRefBook);
    } else if (editMode) {
      modalTitle = t(mes.updateRefBook);
    } else {
      modalTitle = values.title;
    }
    return modalTitle;
  };

  return (
    <Modal
      styleName="m.modal l.modal"
      visibility={visibility}
      onClose={onClose}
      label={getModalTitle()}
    >
      <ModalContent styleName="m.modal__content l.modal__content">
        <div styleName="l.content__wrap">
          <Tab
            value={activeTab}
            type="auto"
            underline={true}
            onChange={({ value }) => setActiveTab(value)}
          >
            <TabItem label={t(mes.settings)} value="settings" />
            <TabItem
              label={t(mes.bookUsage)}
              value="usage"
              visibility={editMode ? 'hidden' : 'visible'}
            />
            <TabItem label={t(mes.refBookAccess)} value="access" />
          </Tab>
          <div styleName="l.content">{renderTabContent()}</div>
        </div>
      </ModalContent>
      <Button
        styleName="l.content__editBtn"
        type="text"
        size="smallplus"
        fontWeight="normal"
        label={t(mes.edit)}
        onClick={() => {
          setEditMode(true);
          if (activeTab === 'usage') {
            setActiveTab('settings');
          }
        }}
        visibility={editMode ? 'hidden' : 'visible'}
      />
      {editMode ? (
        <ModalButtons styleName="m.modal__buttons">
          <Button
            label={t(mes.save)}
            size="large"
            fontWeight="normal"
            onClick={handleSubmit}
            visibility={isSubmit ? 'disabled' : 'visible'}
          />
          <ProgressBar
            styleName="m.modal__loader"
            type="circle"
            size="small"
            visibility={isSubmit ? 'visible' : 'hidden'}
          />
        </ModalButtons>
      ) : null}
    </Modal>
  );
}

CreateRefBooksBase.propTypes = {
  data: PropTypes.object,
  visibility: PropTypes.bool,
  onClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleOnChange: PropTypes.func,
  onContentChange: PropTypes.func,
  isSubmit: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
};

const CreateRefBook = withForm(
  {
    mapPropsToValues(props) {
      const formData = props.data ? structuredClone(props.data) : {};
      return {
        title: formData.title || '',
        fields: formData.fields || [],
        access: formData.access || [],
      };
    },
    yup: Yup.object().shape({
      title: Yup.string().required('field_required'),
      fields: Yup.array()
        .of(
          Yup.object().shape({
            value: Yup.string().required('fieldEmpty'),
            title: Yup.string().required('fieldEmpty'),
          })
        )
        .min(1, 'minRefBook'),
    }),
    handleSubmit: (values, dispatch, formActions, props) => {
      const { title, fields, access } = values;
      const { id } = props.data;
      dispatch({
        type: id ? UPDATE_REF_BOOK.REQUEST : CREATE_REF_BOOK.REQUEST,
        payload: { id, title, bookData: fields, access },
      });
      dispatch({ type: DEL_MODAL });
    },
  },
  CreateRefBooksBase
);

export default CreateRefBook;
